import React, { useState, useEffect, useContext } from "react";
import "./App.css";
import BaseRouter from "./routes";
import LoaderStatus from "./containers/apps/loader/Loader";
import { LoaderContext } from "./context/LoaderContext";
import { ThemeProviderContext, useThemeContext } from "./context/ThemeContext";
import { AuthProvider } from "./context/AuthContext";
import { StyledEngineProvider } from "@mui/material";
import { darkTheme, lightTheme } from "./AppTheme";
import { SnackbarProvider } from "notistack";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";

function InternetStatusBanner() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOnlineBanner, setShowOnlineBanner] = useState(false);

  useEffect(() => {
    const goOnline = () => {
      setIsOnline(true);
      setShowOnlineBanner(true);
    };
    const goOffline = () => setIsOnline(false);

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // Auto-hide the "back online" confirmation. This previously scheduled a
  // setTimeout during render, so it re-fired on every re-render.
  useEffect(() => {
    if (!showOnlineBanner) return undefined;
    const timer = setTimeout(() => setShowOnlineBanner(false), 2000);
    return () => clearTimeout(timer);
  }, [showOnlineBanner]);

  if (isOnline && !showOnlineBanner) return null;

  const banner = {
    padding: "10px",
    textAlign: "center",
    position: "fixed",
    top: 0,
    width: "100%",
    zIndex: 9999,
    color: "white",
  };

  return isOnline ? (
    <div style={{ ...banner, backgroundColor: "#4caf50" }}>
      🤖 You are Online. Continue with your work. 🤖
    </div>
  ) : (
    <div style={{ ...banner, backgroundColor: "#ff4d4f" }}>
      ⚠️ You are offline. Some features may not work.
    </div>
  );
}

const AppContent = () => {
  const { isLoading } = useContext(LoaderContext);
  const { themeMode } = useThemeContext();

  useEffect(() => {
    if (window.top !== window.self) {
      window.top.location = window.self.location;
    }
  }, []);

  return (
    <div className="App" style={{ fontFamily: "roboto" }}>
      <InternetStatusBanner />
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={themeMode ? darkTheme : lightTheme}>
          <SnackbarProvider
            maxSnack={3}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            <BrowserRouter>
              {/* Inside the router so AuthContext consumers can navigate. */}
              <AuthProvider>
                {isLoading && <LoaderStatus />}
                <BaseRouter />
              </AuthProvider>
            </BrowserRouter>
          </SnackbarProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </div>
  );
};

const App = () => (
  <ThemeProviderContext>
    <AppContent />
  </ThemeProviderContext>
);

export default App;
